// Single source of truth for subscription plans (limits, price, features).
// Used by the in-app Subscription page and the public Pricing page.
// Billing is manual/admin for now — no payment gateway wired yet.

export const PLANS = {
  Free: {
    id: 'Free',
    name: 'Free',
    tagline: 'Fleet management, free',
    monthly: 0,
    annual: 0,
    limits: { vehicles: 3, users: 1, drivers: 3 },
    gradient: 'from-slate-600 to-slate-800',
    accent: '#475569',
    // Running your own vehicles is free. Paid tiers sell scale, growth and branding.
    // Only list what an owner can actually reach today. "tracking", not "alerts" —
    // expiry is a status shown on a page; nothing is scheduled and nothing is sent.
    features: [
      'Bookings & journeys',
      'Drivers & assignments',
      'Maintenance & document tracking',
      'Buy / Sell listings',
      'Public share pages',
      'Basic reports',
      'Community support',
    ],
    saleFeatures: [
      'Buy / Sell listings',
      'Buyer enquiries with OTP',
      'Document expiry tracking',
      'Public share pages',
      'Basic reports',
      'Community support',
    ],
  },
  Basic: {
    id: 'Basic',
    name: 'Basic',
    tagline: 'Growing agency',
    monthly: 299,
    annual: 2999,
    limits: { vehicles: 10, users: 3, drivers: 10 },
    gradient: 'from-blue-500 to-blue-700',
    accent: '#2563eb',
    features: [
      'Everything in Free',
      'Bulk import (CSV / Excel)',
      'Trip packages',
      'WhatsApp share + OTP booking',
      'Email support',
    ],
    saleFeatures: [
      'Everything in Free',
      'Bulk listing import (CSV / Excel)',
      'WhatsApp share + OTP enquiries',
      'Email support',
    ],
  },
  Professional: {
    id: 'Professional',
    name: 'Professional',
    tagline: 'Full operations',
    monthly: 799,
    annual: 7999,
    popular: true,
    limits: { vehicles: 30, users: 10, drivers: Infinity },
    gradient: 'from-indigo-600 to-violet-700',
    accent: '#6d28d9',
    features: [
      'Everything in Basic',
      'One-way trips',
      'Your logo on share pages',
      'Advanced reports & exports',
      'Priority support',
    ],
    saleFeatures: [
      'Everything in Basic',
      'Your logo on share pages',
      'Advanced reports & exports',
      'Priority support',
    ],
  },
  Enterprise: {
    id: 'Enterprise',
    name: 'Enterprise',
    tagline: 'Large / multi-branch',
    monthly: null,
    annual: null,
    custom: true,
    limits: { vehicles: Infinity, users: Infinity, drivers: Infinity },
    gradient: 'from-gray-800 to-gray-950',
    accent: '#111827',
    // Custom domain, multi-branch and API access are NOT built — don't sell them.
    features: [
      'Everything in Professional',
      'Unlimited vehicles, staff & drivers',
      'Dedicated onboarding — we import your fleet',
      'Priority support with response SLA',
    ],
    saleFeatures: [
      'Everything in Professional',
      'Unlimited listings & staff',
      'Dedicated onboarding — we import your listings',
      'Priority support with response SLA',
    ],
  },
};

export const PLAN_ORDER = ['Free', 'Basic', 'Professional', 'Enterprise'];

export const getPlan = (id) => PLANS[id] || PLANS.Free;

// Mirrors backend/src/config/plans.js — an expired paid plan falls back to Free.
// The stored `plan` is never rewritten, so renewing restores everything as it was.
export const activePlanId = (subscription) => {
  const plan = subscription?.plan || 'Free';
  if (!PLANS[plan]) return 'Free';
  const expiresAt = subscription?.expiresAt;
  if (plan !== 'Free' && expiresAt && new Date(expiresAt) < new Date()) return 'Free';
  return plan;
};

export const isExpired = (subscription) => (
  (subscription?.plan || 'Free') !== 'Free' && activePlanId(subscription) === 'Free'
);

// The plan(s) a user can move up to from their current one.
export const upgradesFrom = (id) => {
  const i = PLAN_ORDER.indexOf(id);
  return PLAN_ORDER.slice(i + 1).map((p) => PLANS[p]);
};

export const fmtLimit = (n) => (n === Infinity ? 'Unlimited' : n);

// ── Audience ──────────────────────────────────────────────────────────────
// Prices and caps are identical for everyone. What changes is the wording and
// which rows are worth showing: a Buy/Sell owner has no Drivers page at all,
// so a "drivers" limit is meaningless to them.

export const AUDIENCES = [
  { value: 'rental', label: 'I rent out vehicles' },
  { value: 'sale', label: 'I sell vehicles' },
  { value: 'both', label: 'I do both' },
];

export const audienceOf = (businessType) => (
  businessType === 'sale' ? 'sale' : businessType === 'rental' ? 'rental' : 'both'
);

// A dealer thinks in listings, a fleet owner thinks in vehicles.
export const vehicleNoun = (audience) => (audience === 'sale' ? 'listings' : 'vehicles');

// Drivers only exist in the rental flow (Journeys, trips, assignments).
export const showsDrivers = (audience) => audience !== 'sale';

// `features` is the full list — it already includes "Buy / Sell listings" and bulk
// import, so a 'both' owner sees everything without merging. `saleFeatures` is the
// trimmed list for dealers, who have no bookings, journeys, trips or drivers.
export const planFeatures = (plan, audience) => (
  audience === 'sale' ? (plan.saleFeatures || plan.features) : plan.features
);

// `overrides.vehicles` lets the admin-set per-owner cap win over the plan default.
export const limitChips = (plan, audience, overrides = {}) => {
  const vehicles = overrides.vehicles ?? plan.limits.vehicles;
  const chips = [
    `${fmtLimit(vehicles)} ${vehicleNoun(audience)}`,
    `${fmtLimit(plan.limits.users)} team users`,
  ];
  if (showsDrivers(audience)) chips.push(`${fmtLimit(plan.limits.drivers)} drivers`);
  return chips;
};
